import { useRef, useCallback } from 'react';

interface UsePasteHandlerProps {
  onFileAttach: (file: File) => void;
}

export const usePasteHandler = ({ onFileAttach }: UsePasteHandlerProps) => {
  // Counters for generating unique filenames, similar to the previous frontend
  const pastedImageCounterRef = useRef(1);
  const pastedTextCounterRef = useRef(1);

  const handlePaste = useCallback((e: ClipboardEvent) => {
    if (!e.clipboardData) return;

    const items = Array.from(e.clipboardData.items);
    const filesToAttach: File[] = [];
    let shouldPreventDefault = false;

    // Handle text first (prioritize text over images)
    const text = e.clipboardData.getData('text');
    const LARGE_TEXT_THRESHOLD = 2500;
    
    if (text && text.length > LARGE_TEXT_THRESHOLD) {
      const fileName = `attached-text-${pastedTextCounterRef.current}.txt`;
      const file = new File([text], fileName, { type: 'text/plain' });
      filesToAttach.push(file);
      pastedTextCounterRef.current++;
      shouldPreventDefault = true; // Large text becomes attachment
    } else if (text && text.trim()) {
      // Small text exists - allow normal paste behavior and don't process images
      return; // Exit early, let browser handle normal paste
    }

    // Only process images if there's no text (or only whitespace)
    if (!text || !text.trim()) {
      items.forEach(item => {
        if (item.kind === 'file' && item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            // Use the file directly instead of trying to create a new File object
            // If we need to rename it, create a new File with the blob data
            const ext = item.type.split('/')[1] || 'png';
            const fileName = `attached-image-${pastedImageCounterRef.current}.${ext}`;
            const renamedFile = new File([file], fileName, { type: file.type });
            filesToAttach.push(renamedFile);
            pastedImageCounterRef.current++;
            shouldPreventDefault = true; // Images always become attachments
          }
        }
      });
    }

    // Attach all files and prevent default if needed
    if (filesToAttach.length > 0) {
      filesToAttach.forEach(file => onFileAttach(file));
    }

    // Prevent default only if we have files to attach (images or large text)
    if (shouldPreventDefault) {
      e.preventDefault();
    }
  }, [onFileAttach]);

  // Reset counters (useful when switching between chat/landing or after submission)
  const resetCounters = useCallback(() => {
    pastedImageCounterRef.current = 1;
    pastedTextCounterRef.current = 1;
  }, []);

  return {
    handlePaste,
    resetCounters
  };
}; 